import { z } from 'zod';

import { isValidUsStateCode } from '@/modules/addressVerification';
import { isValidPhoneDisplay } from '@/modules/ui';

// 5-digit ZIP (matches the eligibility ZIP fields elsewhere in onboarding).
const ZIP_PATTERN = /^\d{5}$/;

/**
 * Member Contact form (ME-1256). All fields required except the apartment line.
 * The screen is optional (Skip for now), but once the user submits we require a
 * complete address + a valid 10-digit phone. State must be a real US code so the
 * select can't yield a stray value. Lob verification is layered on top in Phase 2.
 */
export const memberContactSchema = z.object({
  phone: z
    .string()
    .trim()
    .min(1, 'Required')
    .refine(isValidPhoneDisplay, 'Enter a valid phone number as (xxx) xxx-xxxx'),
  addressLine1: z.string().trim().min(1, 'Required'),
  // Optional apartment / suite / unit.
  addressLine2: z.string().trim().optional(),
  city: z.string().trim().min(1, 'Required'),
  state: z.string().min(1, 'Required').refine(isValidUsStateCode, 'Select a valid US state'),
  zip: z
    .string()
    .trim()
    .min(1, 'Required')
    .refine((value) => ZIP_PATTERN.test(value), 'Enter a valid ZIP code'),
});

export type MemberContactFormValues = z.infer<typeof memberContactSchema>;

/** The contact fields the form collects — the keys of the schema above. */
export type MemberContactField = keyof MemberContactFormValues;

/**
 * Every field the form collects, derived from the schema so the field list, the
 * `MemberContactField` type, and the validated form values can't drift apart.
 * `submitMemberContact` walks this list and requires a matching backend question
 * for each; order is the schema's declaration order (the backend is order-agnostic).
 */
export const MEMBER_CONTACT_FIELDS = Object.keys(memberContactSchema.shape) as MemberContactField[];
